import { z } from "zod";
import { bookGenerationSourceBindingV1Schema } from "./book-generation.js";
import { bookProductionProductSchema, bookProductionVersionBindingV1Schema, } from "./book-production-evidence.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const opaqueIdSchema = z.string().trim().min(1).max(240);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Z][A-Z0-9_.]*$/u);
const timestampSchema = z.string().datetime({ offset: true });
function requireUnique(values, context, path, label) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: `${label} must be unique.` });
}
export const bookCreationBriefContractSchema = z.literal("BOOK_CREATION_BRIEF_V1");
export const bookContextConsumptionManifestContractSchema = z.literal("BOOK_CONTEXT_CONSUMPTION_MANIFEST_V1");
export const bookCreationBriefTruthBoundarySchema = z.literal("SUPPLIED_CONTEXT_NOT_AUTHENTICATED");
export const bookCreationDecisionPrivacySchema = z.enum([
    "PUBLIC_SAFE",
    "PRIVATE_PRODUCTION_ONLY",
    "RESTRICTED_REVIEW_ONLY",
]);
export const bookCreationAuthorityRequirementSchema = z.enum([
    "NONE",
    "CUSTOMER",
    "EXACT_SOURCE",
    "CONSENT",
    "SAFETY",
    "PRINCIPAL",
    "PRODUCT_POLICY",
]);
export const bookCreationAuthoritySubjectV1Schema = z
    .object({
    authoritySubjectId: opaqueIdSchema,
    kind: z.enum(["PRINCIPAL", "GUARDIAN", "CONTRIBUTOR", "SOURCE_CUSTODIAN"]),
    revision: z.number().int().positive(),
    identityDigestSha256: sha256Schema,
})
    .strict();
export const bookCreationSourceRecordV1Schema = z
    .object({
    sourceBindingId: opaqueIdSchema,
    kind: z.enum([
        "CUSTOMER_ANSWER_SET",
        "INTERVIEW_TRANSCRIPT",
        "DOCUMENT",
        "MEDIA",
        "RECIPE_SOURCE",
        "PRODUCT_POLICY",
    ]),
    revision: z.number().int().positive(),
    checksumSha256: sha256Schema,
    objectVersionId: z.string().trim().min(1).max(500),
    suppliedByAuthoritySubjectIds: z.array(opaqueIdSchema).max(20),
    provenanceStatus: z.enum(["SUPPLIED_UNVERIFIED", "TRUSTED_ADAPTER_REQUIRED"]),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.suppliedByAuthoritySubjectIds, context, ["suppliedByAuthoritySubjectIds"], "Source authority subject IDs");
});
export const bookCreationConsentRecordV1Schema = z
    .object({
    consentBindingId: opaqueIdSchema,
    subjectAuthorityIds: z.array(opaqueIdSchema).min(1).max(20),
    purpose: safeCodeSchema,
    revision: z.number().int().positive(),
    suppliedStatus: z.enum(["SUPPLIED_GRANTED", "SUPPLIED_WITHHELD", "NOT_SUPPLIED"]),
    evidenceDigestSha256: sha256Schema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.subjectAuthorityIds, context, ["subjectAuthorityIds"], "Consent authority subject IDs");
    if (value.suppliedStatus === "SUPPLIED_GRANTED" && !value.evidenceDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["evidenceDigestSha256"],
            message: "Supplied granted consent requires an evidence digest.",
        });
});
const bookCreationDecisionBaseShape = {
    decisionId: opaqueIdSchema,
    fieldId: safeCodeSchema,
    questionId: opaqueIdSchema,
    revision: z.number().int().positive(),
    materiality: z.enum(["MATERIAL", "OPTIONAL_CONTEXT"]),
    privacy: bookCreationDecisionPrivacySchema,
    authorityRequirement: bookCreationAuthorityRequirementSchema,
    authoritySubjectIds: z.array(opaqueIdSchema).max(20),
    sourceBindingIds: z.array(opaqueIdSchema).max(40),
    consentBindingIds: z.array(opaqueIdSchema).max(20),
};
const answeredDecisionSchema = z
    .object({
    ...bookCreationDecisionBaseShape,
    resolution: z.literal("ANSWER"),
    origin: z.enum([
        "CUSTOMER",
        "AUTHORIZED_CONTRIBUTOR",
        "EXACT_SOURCE",
        "PRODUCT_POLICY",
        "MODEL_SUGGESTION",
    ]),
    status: z.enum(["SUPPLIED", "CONFIRMED", "DISPUTED"]),
    valueDigestSha256: sha256Schema,
    orderedElementDigestsSha256: z.array(sha256Schema).min(2).max(200).optional(),
})
    .strict();
const unknownDecisionSchema = z
    .object({
    ...bookCreationDecisionBaseShape,
    resolution: z.literal("UNKNOWN"),
    origin: z.enum(["CUSTOMER", "AUTHORIZED_CONTRIBUTOR", "EXACT_SOURCE"]),
    status: z.literal("UNRESOLVED"),
    reasonCode: safeCodeSchema,
})
    .strict();
const excludedDecisionSchema = z
    .object({
    ...bookCreationDecisionBaseShape,
    resolution: z.literal("EXPLICIT_EXCLUSION"),
    origin: z.enum(["CUSTOMER", "AUTHORIZED_CONTRIBUTOR"]),
    status: z.literal("CONFIRMED"),
    reasonCode: safeCodeSchema,
})
    .strict();
const fallbackDecisionSchema = z
    .object({
    ...bookCreationDecisionBaseShape,
    resolution: z.literal("DOCUMENTED_FALLBACK"),
    origin: z.literal("DOCUMENTED_FALLBACK"),
    status: z.literal("PROVISIONAL"),
    reasonCode: safeCodeSchema,
    valueDigestSha256: sha256Schema,
    fallbackPolicyBinding: bookProductionVersionBindingV1Schema,
})
    .strict();
export const bookCreationDecisionV1Schema = z
    .discriminatedUnion("resolution", [
    answeredDecisionSchema,
    unknownDecisionSchema,
    excludedDecisionSchema,
    fallbackDecisionSchema,
])
    .superRefine((value, context) => {
    requireUnique(value.authoritySubjectIds, context, ["authoritySubjectIds"], "Authority IDs");
    requireUnique(value.sourceBindingIds, context, ["sourceBindingIds"], "Source binding IDs");
    requireUnique(value.consentBindingIds, context, ["consentBindingIds"], "Consent binding IDs");
    const restricted = ["CUSTOMER", "EXACT_SOURCE", "CONSENT", "SAFETY", "PRINCIPAL"];
    if ((value.resolution === "DOCUMENTED_FALLBACK" ||
        (value.resolution === "ANSWER" &&
            (value.origin === "MODEL_SUGGESTION" || value.origin === "PRODUCT_POLICY"))) &&
        restricted.includes(value.authorityRequirement))
        context.addIssue({
            code: "custom",
            path: ["authorityRequirement"],
            message: "Model, product-policy, and fallback values cannot satisfy customer, source, consent, safety, or principal authority.",
        });
    if (value.authorityRequirement === "SAFETY") {
        if (value.resolution === "EXPLICIT_EXCLUSION" || value.resolution === "DOCUMENTED_FALLBACK")
            context.addIssue({
                code: "custom",
                path: ["resolution"],
                message: "Safety may be exact-source answered or remain explicitly unknown; it cannot be excluded or defaulted.",
            });
        if (value.resolution === "ANSWER" && value.origin !== "EXACT_SOURCE")
            context.addIssue({
                code: "custom",
                path: ["origin"],
                message: "An answered safety decision requires exact-source origin.",
            });
    }
    if (value.resolution === "ANSWER" &&
        (value.authorityRequirement === "EXACT_SOURCE" || value.authorityRequirement === "SAFETY") &&
        (value.origin !== "EXACT_SOURCE" || value.sourceBindingIds.length === 0))
        context.addIssue({
            code: "custom",
            path: ["sourceBindingIds"],
            message: "Exact-source and safety answers require an exact supplied source binding.",
        });
    if (value.resolution === "ANSWER" &&
        value.authorityRequirement === "CONSENT" &&
        (value.authoritySubjectIds.length === 0 ||
            value.consentBindingIds.length === 0 ||
            !["CUSTOMER", "AUTHORIZED_CONTRIBUTOR"].includes(value.origin)))
        context.addIssue({
            code: "custom",
            path: ["consentBindingIds"],
            message: "A consent answer requires eligible customer authority subjects and supplied consent bindings.",
        });
    if (value.resolution === "ANSWER" &&
        (value.authorityRequirement === "CUSTOMER" || value.authorityRequirement === "PRINCIPAL") &&
        (value.authoritySubjectIds.length === 0 ||
            !["CUSTOMER", "AUTHORIZED_CONTRIBUTOR"].includes(value.origin)))
        context.addIssue({
            code: "custom",
            path: ["authoritySubjectIds"],
            message: "Customer and principal answers require an eligible supplied authority subject.",
        });
    if (value.resolution === "UNKNOWN" &&
        value.origin === "EXACT_SOURCE" &&
        value.sourceBindingIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["sourceBindingIds"],
            message: "An exact-source unknown requires the exact supplied source that was inspected.",
        });
    if (value.resolution === "UNKNOWN" &&
        value.authorityRequirement === "SAFETY" &&
        (value.origin !== "EXACT_SOURCE" || value.sourceBindingIds.length === 0))
        context.addIssue({
            code: "custom",
            path: ["sourceBindingIds"],
            message: "An unresolved safety decision must remain unknown and bind the exact supplied source reviewed.",
        });
    if (value.resolution === "UNKNOWN" &&
        value.authorityRequirement === "PRINCIPAL" &&
        (value.authoritySubjectIds.length === 0 ||
            !["CUSTOMER", "AUTHORIZED_CONTRIBUTOR"].includes(value.origin)))
        context.addIssue({
            code: "custom",
            path: ["authoritySubjectIds"],
            message: "A principal-held unknown must identify the principal who supplied it.",
        });
    if (value.resolution === "EXPLICIT_EXCLUSION" &&
        (value.authorityRequirement === "CUSTOMER" ||
            value.authorityRequirement === "PRINCIPAL" ||
            value.authorityRequirement === "CONSENT") &&
        value.authoritySubjectIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["authoritySubjectIds"],
            message: "A customer, principal, or consent-related exclusion requires its supplied authority subject.",
        });
    if (value.resolution === "ANSWER" &&
        restricted.includes(value.authorityRequirement) &&
        value.status !== "CONFIRMED")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A protected-authority answer must be explicitly confirmed in the supplied inputs.",
        });
    if (value.resolution === "ANSWER" &&
        value.authorityRequirement === "PRODUCT_POLICY" &&
        value.origin !== "PRODUCT_POLICY")
        context.addIssue({
            code: "custom",
            path: ["origin"],
            message: "A product-policy answer must identify product-policy origin.",
        });
});
export const bookCreationCommonDecisionRefsV1Schema = z
    .object({
    titleIntentDecisionId: opaqueIdSchema,
    purposeDecisionId: opaqueIdSchema,
    audienceDecisionId: opaqueIdSchema,
    localeLanguageDecisionId: opaqueIdSchema,
    outputIntentDecisionId: opaqueIdSchema,
    accessibilityDecisionId: opaqueIdSchema,
    privacyDecisionId: opaqueIdSchema,
    sourceInventoryDecisionId: opaqueIdSchema,
    mediaInventoryDecisionId: opaqueIdSchema,
    mustIncludeDecisionId: opaqueIdSchema,
    mustAvoidDecisionId: opaqueIdSchema,
})
    .strict();
const bedtimeCreationContextV1Schema = z
    .object({
    product: z.literal("BEDTIME"),
    ageDevelopmentDecisionId: opaqueIdSchema,
    readingModeDecisionId: opaqueIdSchema,
    emotionalCeilingDecisionId: opaqueIdSchema,
    fearSensoryDecisionId: opaqueIdSchema,
    realityBoundaryDecisionId: opaqueIdSchema,
    likenessGuardianDecisionId: opaqueIdSchema,
    bedtimeLandingDecisionId: opaqueIdSchema,
    pronunciationDecisionId: opaqueIdSchema,
})
    .strict();
const comicCreationContextV1Schema = z
    .object({
    product: z.literal("COMIC"),
    genreEraDecisionId: opaqueIdSchema,
    contentBoundaryDecisionId: opaqueIdSchema,
    castLikenessDecisionId: opaqueIdSchema,
    continuityDecisionId: opaqueIdSchema,
    chronologyDecisionId: opaqueIdSchema,
    visualMediumDecisionId: opaqueIdSchema,
    panelRhythmDecisionId: opaqueIdSchema,
    letteringSfxDecisionId: opaqueIdSchema,
})
    .strict();
const recipeCreationContextV1Schema = z
    .object({
    product: z.literal("RECIPE"),
    localeUnitsDecisionId: opaqueIdSchema,
    sourceFidelityDecisionId: opaqueIdSchema,
    quantityYieldDecisionId: opaqueIdSchema,
    skillEquipmentDecisionId: opaqueIdSchema,
    allergenDecisionId: opaqueIdSchema,
    testedStatusDecisionId: opaqueIdSchema,
    temperatureDonenessDecisionId: opaqueIdSchema,
    storagePreservationDecisionId: opaqueIdSchema,
    substitutionDecisionId: opaqueIdSchema,
    collectionOrganizationDecisionId: opaqueIdSchema,
})
    .strict();
const myStoryCreationContextV1Schema = z
    .object({
    product: z.literal("MY_STORY"),
    principalAuthoritySubjectIds: z.tuple([opaqueIdSchema]),
    intendedReadersDecisionId: opaqueIdSchema,
    narrativeScopeDecisionId: opaqueIdSchema,
    chronologyUncertaintyDecisionId: opaqueIdSchema,
    voicePovDecisionId: opaqueIdSchema,
    correctionDecisionId: opaqueIdSchema,
    exclusionDecisionId: opaqueIdSchema,
    evidenceVisibilityDecisionId: opaqueIdSchema,
    mediaConsentDecisionId: opaqueIdSchema,
})
    .strict();
const ourStoryCreationContextV1Schema = z
    .object({
    product: z.literal("OUR_STORY"),
    principalAuthoritySubjectIds: z
        .tuple([opaqueIdSchema, opaqueIdSchema])
        .superRefine((value, context) => requireUnique(value, context, [], "Our Story principal authority subject IDs")),
    captureModeDecisionId: opaqueIdSchema,
    revealPolicyDecisionId: opaqueIdSchema,
    differingMemoryDecisionId: opaqueIdSchema,
    sharedClaimDecisionId: opaqueIdSchema,
    privateExclusionDecisionId: opaqueIdSchema,
    voiceAttributionDecisionId: opaqueIdSchema,
    mutualMediaConsentDecisionId: opaqueIdSchema,
    balanceDecisionId: opaqueIdSchema,
    releaseApprovalDecisionId: opaqueIdSchema,
})
    .strict();
export const bookCreationProductContextV1Schema = z.discriminatedUnion("product", [
    bedtimeCreationContextV1Schema,
    comicCreationContextV1Schema,
    recipeCreationContextV1Schema,
    myStoryCreationContextV1Schema,
    ourStoryCreationContextV1Schema,
]);
const commonFieldByRef = {
    titleIntentDecisionId: "COMMON.TITLE_INTENT",
    purposeDecisionId: "COMMON.PURPOSE",
    audienceDecisionId: "COMMON.AUDIENCE",
    localeLanguageDecisionId: "COMMON.LOCALE_LANGUAGE",
    outputIntentDecisionId: "COMMON.OUTPUT_INTENT",
    accessibilityDecisionId: "COMMON.ACCESSIBILITY",
    privacyDecisionId: "COMMON.PRIVACY",
    sourceInventoryDecisionId: "COMMON.SOURCE_INVENTORY",
    mediaInventoryDecisionId: "COMMON.MEDIA_INVENTORY",
    mustIncludeDecisionId: "COMMON.MUST_INCLUDE",
    mustAvoidDecisionId: "COMMON.MUST_AVOID",
};
const productFieldByRef = {
    BEDTIME: {
        ageDevelopmentDecisionId: "BEDTIME.AGE_DEVELOPMENT",
        readingModeDecisionId: "BEDTIME.READING_MODE",
        emotionalCeilingDecisionId: "BEDTIME.EMOTIONAL_CEILING",
        fearSensoryDecisionId: "BEDTIME.FEAR_SENSORY",
        realityBoundaryDecisionId: "BEDTIME.REALITY_BOUNDARY",
        likenessGuardianDecisionId: "BEDTIME.LIKENESS_GUARDIAN",
        bedtimeLandingDecisionId: "BEDTIME.LANDING",
        pronunciationDecisionId: "BEDTIME.PRONUNCIATION",
    },
    COMIC: {
        genreEraDecisionId: "COMIC.GENRE_ERA",
        contentBoundaryDecisionId: "COMIC.CONTENT_BOUNDARY",
        castLikenessDecisionId: "COMIC.CAST_LIKENESS",
        continuityDecisionId: "COMIC.CONTINUITY",
        chronologyDecisionId: "COMIC.CHRONOLOGY",
        visualMediumDecisionId: "COMIC.VISUAL_MEDIUM",
        panelRhythmDecisionId: "COMIC.PANEL_RHYTHM",
        letteringSfxDecisionId: "COMIC.LETTERING_SFX",
    },
    RECIPE: {
        localeUnitsDecisionId: "RECIPE.LOCALE_UNITS",
        sourceFidelityDecisionId: "RECIPE.SOURCE_FIDELITY",
        quantityYieldDecisionId: "RECIPE.QUANTITY_YIELD",
        skillEquipmentDecisionId: "RECIPE.SKILL_EQUIPMENT",
        allergenDecisionId: "RECIPE.ALLERGEN",
        testedStatusDecisionId: "RECIPE.TESTED_STATUS",
        temperatureDonenessDecisionId: "RECIPE.TEMPERATURE_DONENESS",
        storagePreservationDecisionId: "RECIPE.STORAGE_PRESERVATION",
        substitutionDecisionId: "RECIPE.SUBSTITUTION",
        collectionOrganizationDecisionId: "RECIPE.COLLECTION_ORGANIZATION",
    },
    MY_STORY: {
        intendedReadersDecisionId: "MY_STORY.INTENDED_READERS",
        narrativeScopeDecisionId: "MY_STORY.NARRATIVE_SCOPE",
        chronologyUncertaintyDecisionId: "MY_STORY.CHRONOLOGY_UNCERTAINTY",
        voicePovDecisionId: "MY_STORY.VOICE_POV",
        correctionDecisionId: "MY_STORY.CORRECTION",
        exclusionDecisionId: "MY_STORY.EXCLUSION",
        evidenceVisibilityDecisionId: "MY_STORY.EVIDENCE_VISIBILITY",
        mediaConsentDecisionId: "MY_STORY.MEDIA_CONSENT",
    },
    OUR_STORY: {
        captureModeDecisionId: "OUR_STORY.CAPTURE_MODE",
        revealPolicyDecisionId: "OUR_STORY.REVEAL_POLICY",
        differingMemoryDecisionId: "OUR_STORY.DIFFERING_MEMORY",
        sharedClaimDecisionId: "OUR_STORY.SHARED_CLAIM",
        privateExclusionDecisionId: "OUR_STORY.PRIVATE_EXCLUSION",
        voiceAttributionDecisionId: "OUR_STORY.VOICE_ATTRIBUTION",
        mutualMediaConsentDecisionId: "OUR_STORY.MUTUAL_MEDIA_CONSENT",
        balanceDecisionId: "OUR_STORY.BALANCE",
        releaseApprovalDecisionId: "OUR_STORY.RELEASE_APPROVAL",
    },
};
const protectedFieldAuthority = {
    "BEDTIME.LIKENESS_GUARDIAN": "CONSENT",
    "RECIPE.SOURCE_FIDELITY": "EXACT_SOURCE",
    "RECIPE.ALLERGEN": "SAFETY",
    "RECIPE.TESTED_STATUS": "SAFETY",
    "RECIPE.TEMPERATURE_DONENESS": "SAFETY",
    "RECIPE.STORAGE_PRESERVATION": "SAFETY",
    "MY_STORY.VOICE_POV": "PRINCIPAL",
    "MY_STORY.EXCLUSION": "PRINCIPAL",
    "MY_STORY.MEDIA_CONSENT": "CONSENT",
    "OUR_STORY.REVEAL_POLICY": "PRINCIPAL",
    "OUR_STORY.DIFFERING_MEMORY": "PRINCIPAL",
    "OUR_STORY.SHARED_CLAIM": "PRINCIPAL",
    "OUR_STORY.PRIVATE_EXCLUSION": "PRINCIPAL",
    "OUR_STORY.MUTUAL_MEDIA_CONSENT": "CONSENT",
    "OUR_STORY.RELEASE_APPROVAL": "CONSENT",
};
const ourStoryJointPrincipalFields = new Set([
    "OUR_STORY.REVEAL_POLICY",
    "OUR_STORY.DIFFERING_MEMORY",
    "OUR_STORY.SHARED_CLAIM",
    "OUR_STORY.MUTUAL_MEDIA_CONSENT",
    "OUR_STORY.RELEASE_APPROVAL",
]);
function productDecisionRefs(productContext) {
    return Object.entries(productContext).filter(([key, value]) => key.endsWith("DecisionId") && typeof value === "string");
}
export const bookCreationBriefBodyV1Schema = z
    .object({
    contract: bookCreationBriefContractSchema,
    briefId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    briefRevision: z.number().int().positive(),
    product: bookProductionProductSchema,
    immutable: z.literal(true),
    compiledAt: timestampSchema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    compilerBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    authoritySubjects: z.array(bookCreationAuthoritySubjectV1Schema).min(1).max(40),
    sourceRecords: z.array(bookCreationSourceRecordV1Schema).min(1).max(200),
    consentRecords: z.array(bookCreationConsentRecordV1Schema).max(100),
    decisions: z.array(bookCreationDecisionV1Schema).min(1).max(300),
    commonDecisionRefs: bookCreationCommonDecisionRefsV1Schema,
    productContext: bookCreationProductContextV1Schema,
    truthBoundary: bookCreationBriefTruthBoundarySchema,
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.authoritySubjects.map(({ authoritySubjectId }) => authoritySubjectId), context, ["authoritySubjects"], "Authority subject IDs");
    requireUnique(value.sourceRecords.map(({ sourceBindingId }) => sourceBindingId), context, ["sourceRecords"], "Source binding IDs");
    requireUnique(value.consentRecords.map(({ consentBindingId }) => consentBindingId), context, ["consentRecords"], "Consent binding IDs");
    requireUnique(value.decisions.map(({ decisionId }) => decisionId), context, ["decisions"], "Decision IDs");
    if (value.productContext.product !== value.product)
        context.addIssue({
            code: "custom",
            path: ["productContext", "product"],
            message: "The creation context must use the brief product discriminator.",
        });
    const authoritySubjects = new Map(value.authoritySubjects.map((subject) => [subject.authoritySubjectId, subject]));
    const sourceRecords = new Map(value.sourceRecords.map((source) => [source.sourceBindingId, source]));
    const consentRecords = new Map(value.consentRecords.map((consent) => [consent.consentBindingId, consent]));
    const decisions = new Map(value.decisions.map((decision) => [decision.decisionId, decision]));
    for (const source of value.sourceRecords)
        for (const subjectId of source.suppliedByAuthoritySubjectIds)
            if (!authoritySubjects.has(subjectId))
                context.addIssue({
                    code: "custom",
                    path: ["sourceRecords"],
                    message: `Source ${source.sourceBindingId} references unknown authority subject ${subjectId}.`,
                });
    for (const consent of value.consentRecords)
        for (const subjectId of consent.subjectAuthorityIds)
            if (!authoritySubjects.has(subjectId))
                context.addIssue({
                    code: "custom",
                    path: ["consentRecords"],
                    message: `Consent ${consent.consentBindingId} references unknown authority subject ${subjectId}.`,
                });
    for (const decision of value.decisions) {
        const protectedRequirement = protectedFieldAuthority[decision.fieldId];
        if (protectedRequirement && decision.authorityRequirement !== protectedRequirement)
            context.addIssue({
                code: "custom",
                path: ["decisions"],
                message: `${decision.fieldId} requires ${protectedRequirement} authority semantics.`,
            });
        for (const subjectId of decision.authoritySubjectIds)
            if (!authoritySubjects.has(subjectId))
                context.addIssue({
                    code: "custom",
                    path: ["decisions"],
                    message: `Decision ${decision.decisionId} references unknown authority subject ${subjectId}.`,
                });
        for (const sourceBindingId of decision.sourceBindingIds)
            if (!sourceRecords.has(sourceBindingId))
                context.addIssue({
                    code: "custom",
                    path: ["decisions"],
                    message: `Decision ${decision.decisionId} references unknown source ${sourceBindingId}.`,
                });
        for (const consentBindingId of decision.consentBindingIds) {
            const consent = consentRecords.get(consentBindingId);
            if (!consent)
                context.addIssue({
                    code: "custom",
                    path: ["decisions"],
                    message: `Decision ${decision.decisionId} references unknown consent ${consentBindingId}.`,
                });
            else if (decision.authorityRequirement === "CONSENT" &&
                consent.suppliedStatus !== "SUPPLIED_GRANTED")
                context.addIssue({
                    code: "custom",
                    path: ["decisions"],
                    message: `Decision ${decision.decisionId} cannot treat ungranted supplied consent as consent.`,
                });
            else if (decision.authorityRequirement === "CONSENT" &&
                decision.authoritySubjectIds.some((subjectId) => !consent.subjectAuthorityIds.includes(subjectId)))
                context.addIssue({
                    code: "custom",
                    path: ["decisions"],
                    message: `Decision ${decision.decisionId} has authority subjects outside consent ${consentBindingId}.`,
                });
        }
        if (decision.authorityRequirement === "PRINCIPAL")
            for (const subjectId of decision.authoritySubjectIds)
                if (authoritySubjects.get(subjectId)?.kind !== "PRINCIPAL")
                    context.addIssue({
                        code: "custom",
                        path: ["decisions"],
                        message: `Decision ${decision.decisionId} requires principal authority subjects.`,
                    });
    }
    const refs = [];
    for (const [refName, expectedField] of Object.entries(commonFieldByRef))
        refs.push([
            refName,
            value.commonDecisionRefs[refName],
            expectedField,
        ]);
    for (const [refName, decisionId] of productDecisionRefs(value.productContext)) {
        const expectedField = productFieldByRef[value.product][refName];
        if (expectedField)
            refs.push([refName, decisionId, expectedField]);
    }
    requireUnique(refs.map(([, decisionId]) => decisionId), context, ["productContext"], "Common and product decision references");
    for (const [refName, decisionId, expectedField] of refs) {
        const decision = decisions.get(decisionId);
        if (!decision)
            context.addIssue({
                code: "custom",
                path: ["productContext", refName],
                message: `Decision reference ${decisionId} does not exist.`,
            });
        else if (decision.fieldId !== expectedField)
            context.addIssue({
                code: "custom",
                path: ["productContext", refName],
                message: `${refName} must bind ${expectedField}, not ${decision.fieldId}.`,
            });
    }
    for (const decision of value.decisions)
        if (!refs.some(([, decisionId]) => decisionId === decision.decisionId))
            context.addIssue({
                code: "custom",
                path: ["decisions"],
                message: `Decision ${decision.decisionId} is not classified by the common or product context.`,
            });
    const principals = value.authoritySubjects.filter(({ kind }) => kind === "PRINCIPAL");
    if (value.product === "MY_STORY" || value.product === "OUR_STORY") {
        const expected = value.product === "MY_STORY" ? 1 : 2;
        if (principals.length !== expected)
            context.addIssue({
                code: "custom",
                path: ["authoritySubjects"],
                message: `${value.product} requires exactly ${expected} principal authority subject${expected === 1 ? "" : "s"}.`,
            });
        if (value.productContext.product === value.product) {
            const declared = value.productContext.principalAuthoritySubjectIds;
            const declaredSet = new Set(declared);
            for (const subjectId of declared)
                if (authoritySubjects.get(subjectId)?.kind !== "PRINCIPAL")
                    context.addIssue({
                        code: "custom",
                        path: ["productContext", "principalAuthoritySubjectIds"],
                        message: `${subjectId} is not a declared principal authority subject.`,
                    });
            if (principals.some(({ authoritySubjectId }) => !declaredSet.has(authoritySubjectId)))
                context.addIssue({
                    code: "custom",
                    path: ["productContext", "principalAuthoritySubjectIds"],
                    message: "The product context must bind every and only the declared principals.",
                });
            if (value.product === "OUR_STORY")
                for (const decision of value.decisions)
                    if (ourStoryJointPrincipalFields.has(decision.fieldId) &&
                        (decision.authoritySubjectIds.length !== declaredSet.size ||
                            decision.authoritySubjectIds.some((subjectId) => !declaredSet.has(subjectId))))
                        context.addIssue({
                            code: "custom",
                            path: ["decisions"],
                            message: `${decision.fieldId} must bind both declared Our Story principals; one principal cannot stand in for the other.`,
                        });
        }
    }
});
export const bookCreationBriefV1Schema = bookCreationBriefBodyV1Schema.safeExtend({
    briefDigestSha256: sha256Schema,
});
export const bookContextConsumerSchema = z.enum([
    "EDITORIAL_INTENT",
    "VISUAL_DIRECTION",
    "MEDIA_POLICY",
    "COMPOSITION_POLICY",
    "OUTPUT_PROFILE",
    "REVIEW_REQUIREMENT",
]);
export const bookContextConsumptionUseV1Schema = z
    .object({
    decisionId: opaqueIdSchema,
    decisionRevision: z.number().int().positive(),
    consumer: bookContextConsumerSchema,
    purposeCode: safeCodeSchema,
    implementationBinding: bookProductionVersionBindingV1Schema,
})
    .strict();
export const bookContextDispositionV1Schema = z
    .object({
    decisionId: opaqueIdSchema,
    decisionRevision: z.number().int().positive(),
    disposition: z.enum(["DEFERRED", "EXCLUDED_FROM_OUTPUT"]),
    reasonCode: safeCodeSchema,
    policyBinding: bookProductionVersionBindingV1Schema,
})
    .strict();
export const bookContextDefaultDeclarationV1Schema = z
    .object({
    defaultId: opaqueIdSchema,
    fieldId: safeCodeSchema,
    visibility: z.enum(["HIDDEN_MECHANICAL", "VISIBLE_CUSTOMER_EXPERIENCED"]),
    defaultClass: z.enum([
        "MECHANICAL",
        "ACCESSIBILITY_PRESERVING",
        "PRODUCT_FALLBACK",
        "MODEL_SUGGESTED",
    ]),
    valueDigestSha256: sha256Schema,
    fallbackDecisionId: opaqueIdSchema.optional(),
    implementationBinding: bookProductionVersionBindingV1Schema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.visibility === "VISIBLE_CUSTOMER_EXPERIENCED" &&
        (value.defaultClass !== "PRODUCT_FALLBACK" || !value.fallbackDecisionId))
        context.addIssue({
            code: "custom",
            path: ["fallbackDecisionId"],
            message: "A visible product default must be a declared fallback with a causal decision.",
        });
    if (value.defaultClass === "MODEL_SUGGESTED" && value.visibility !== "HIDDEN_MECHANICAL")
        context.addIssue({
            code: "custom",
            path: ["visibility"],
            message: "A model suggestion cannot silently become a visible product default.",
        });
});
export const bookContextDefaultObservationV1Schema = z
    .object({
    defaultId: opaqueIdSchema,
    fieldId: safeCodeSchema,
    visibility: z.enum(["HIDDEN_MECHANICAL", "VISIBLE_CUSTOMER_EXPERIENCED"]),
    valueDigestSha256: sha256Schema,
    causalDecisionId: opaqueIdSchema.optional(),
    causalDecisionRevision: z.number().int().positive().optional(),
    implementationBinding: bookProductionVersionBindingV1Schema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.visibility === "VISIBLE_CUSTOMER_EXPERIENCED" &&
        (!value.causalDecisionId || !value.causalDecisionRevision))
        context.addIssue({
            code: "custom",
            path: ["causalDecisionId"],
            message: "An observed visible default requires an exact causal decision and revision.",
        });
});
export const bookContextConsumptionManifestBodyV1Schema = z
    .object({
    contract: bookContextConsumptionManifestContractSchema,
    manifestId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    briefId: opaqueIdSchema,
    briefDigestSha256: sha256Schema,
    compilerBinding: bookProductionVersionBindingV1Schema,
    uses: z.array(bookContextConsumptionUseV1Schema).max(2_000),
    dispositions: z.array(bookContextDispositionV1Schema).max(300),
    defaultDeclarations: z.array(bookContextDefaultDeclarationV1Schema).max(200),
    observedDefaults: z.array(bookContextDefaultObservationV1Schema).max(500),
    truthBoundary: bookCreationBriefTruthBoundarySchema,
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.dispositions.map(({ decisionId }) => decisionId), context, ["dispositions"], "Disposition decision IDs");
    requireUnique(value.defaultDeclarations.map(({ defaultId }) => defaultId), context, ["defaultDeclarations"], "Default declaration IDs");
});
export const bookContextConsumptionManifestV1Schema = bookContextConsumptionManifestBodyV1Schema.safeExtend({ manifestDigestSha256: sha256Schema });
//# sourceMappingURL=book-creation-brief.js.map